import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '../context/UserContext';

const API = process.env.REACT_APP_API_URL || '';

const POS_COLOR = {
  QB: 'var(--accent2)',
  RB: '#00d4aa',
  WR: '#4fc3f7',
  TE: '#ce93d8',
};

const POS_BG = {
  QB: 'rgba(var(--accent2-rgb),0.15)',
  RB: 'rgba(0,212,170,0.15)',
  WR: 'rgba(79,195,247,0.15)',
  TE: 'rgba(206,147,216,0.15)',
};

const TIER_SIZE = { 1: 14, 2: 11, 3: 9, 4: 7, 5: 5 };

function PosBadge({ pos }) {
  return (
    <span style={{
      background: POS_BG[pos] || 'var(--bg3)',
      color: POS_COLOR[pos] || 'var(--text3)',
      border: `1px solid ${POS_COLOR[pos] || 'var(--border)'}`,
      borderRadius: 4, fontSize: 10, fontWeight: 700,
      padding: '1px 6px', letterSpacing: 0.8,
    }}>
      {pos}
    </span>
  );
}

function AgeScoreBar({ score, label, color }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>Dynasty Age Score</span>
        <span style={{ fontSize: 11, fontWeight: 700, color }}>{score} — {label}</span>
      </div>
      <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 3 }}>
        <div style={{
          height: '100%', borderRadius: 3,
          width: `${score}%`,
          background: `linear-gradient(90deg, ${color}88, ${color})`,
        }} />
      </div>
    </div>
  );
}

// ── Scatter Chart ─────────────────────────────────────────────────────────────
function AgeCurveChart({ players, curveLines, filterPos, filterManager, hoveredPlayer, setHoveredPlayer }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const W = 760, H = 400;
  const PAD = { top: 20, right: 30, bottom: 50, left: 50 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const AGE_MIN = 20, AGE_MAX = 38;
  const SCORE_MIN = 0, SCORE_MAX = 100;

  const xScale = (age) => ((age - AGE_MIN) / (AGE_MAX - AGE_MIN)) * innerW;
  const yScale = (score) => innerH - ((score - SCORE_MIN) / (SCORE_MAX - SCORE_MIN)) * innerH;

  const filteredPlayers = players.filter(p => {
    if (filterPos !== 'ALL' && p.position !== filterPos) return false;
    if (filterManager !== 'ALL' && p.manager !== filterManager) return false;
    return true;
  });

  // Build curve paths
  const curvePaths = Object.entries(curveLines || {}).map(([pos, points]) => {
    if (filterPos !== 'ALL' && filterPos !== pos) return null;
    const d = points.map((pt, i) =>
      `${i === 0 ? 'M' : 'L'} ${xScale(pt.age)} ${yScale(pt.score)}`
    ).join(' ');
    return { pos, d };
  }).filter(Boolean);

  // X axis ticks
  const xTicks = Array.from({ length: 10 }, (_, i) => 20 + i * 2);
  // Y axis ticks
  const yTicks = [0, 25, 50, 75, 100];

  const handleMouseMove = (e, player) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      player,
    });
    setHoveredPlayer(player.player_id);
  };

  const handleMouseLeave = () => {
    setTooltip(null);
    setHoveredPlayer(null);
  };

  return (
    <div style={{ position: 'relative', overflowX: 'auto' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', maxWidth: W, display: 'block' }}
        onMouseLeave={handleMouseLeave}
      >
        <g transform={`translate(${PAD.left},${PAD.top})`}>
          {/* Grid lines */}
          {yTicks.map(tick => (
            <g key={tick}>
              <line x1={0} y1={yScale(tick)} x2={innerW} y2={yScale(tick)}
                    stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
              <text x={-8} y={yScale(tick)} fill="rgba(255,255,255,0.25)"
                    fontSize={10} textAnchor="end" dominantBaseline="middle">
                {tick}
              </text>
            </g>
          ))}
          {xTicks.map(tick => (
            <g key={tick}>
              <line x1={xScale(tick)} y1={0} x2={xScale(tick)} y2={innerH}
                    stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
              <text x={xScale(tick)} y={innerH + 16} fill="rgba(255,255,255,0.25)"
                    fontSize={10} textAnchor="middle">
                {tick}
              </text>
            </g>
          ))}

          {/* Axes */}
          <line x1={0} y1={innerH} x2={innerW} y2={innerH} stroke="rgba(255,255,255,0.1)" strokeWidth={1} />
          <line x1={0} y1={0} x2={0} y2={innerH} stroke="rgba(255,255,255,0.1)" strokeWidth={1} />

          {/* Axis labels */}
          <text x={innerW / 2} y={innerH + 38} fill="rgba(255,255,255,0.3)"
                fontSize={11} textAnchor="middle">Age</text>
          <text x={-innerH / 2} y={-36} fill="rgba(255,255,255,0.3)"
                fontSize={11} textAnchor="middle" transform="rotate(-90)">
            Dynasty Age Score
          </text>

          {/* Peak age markers */}
          {(filterPos === 'ALL' ? ['QB', 'RB', 'WR', 'TE'] : [filterPos]).map(pos => {
            const peaks = { QB: 28, RB: 24, WR: 26, TE: 27 };
            const peak = peaks[pos];
            return (
              <g key={pos}>
                <line x1={xScale(peak)} y1={0} x2={xScale(peak)} y2={innerH}
                      stroke={POS_COLOR[pos]} strokeWidth={1} strokeDasharray="4,4" opacity={0.3} />
                <text x={xScale(peak)} y={-6} fill={POS_COLOR[pos]}
                      fontSize={9} textAnchor="middle" opacity={0.6}>
                  {pos} peak
                </text>
              </g>
            );
          })}

          {/* Curve lines */}
          {curvePaths.map(({ pos, d }) => (
            <path key={pos} d={d} fill="none"
                  stroke={POS_COLOR[pos]} strokeWidth={1.5}
                  strokeDasharray="6,3" opacity={0.4} />
          ))}

          {/* Player dots */}
          {filteredPlayers.map(p => {
            const cx = xScale(p.age);
            const cy = yScale(p.age_score);
            const r = TIER_SIZE[p.tier] || 6;
            const color = POS_COLOR[p.position] || '#888';
            const isHovered = hoveredPlayer === p.player_id;
            const isDimmed = hoveredPlayer && !isHovered;
            return (
              <g key={p.player_id}
                 onMouseMove={(e) => handleMouseMove(e, p)}
                 style={{ cursor: 'pointer' }}>
                {isHovered && (
                  <circle cx={cx} cy={cy} r={r + 6}
                          fill={color} opacity={0.15} />
                )}
                <circle
                  cx={cx} cy={cy} r={isHovered ? r + 2 : r}
                  fill={color}
                  fillOpacity={isDimmed ? 0.15 : p.tier === 1 ? 0.9 : 0.7}
                  stroke={isHovered ? '#fff' : color}
                  strokeWidth={isHovered ? 1.5 : p.tier <= 2 ? 1 : 0}
                  strokeOpacity={0.5}
                />
                {/* Show name for T1 players or hovered */}
                {(p.tier === 1 || isHovered) && !isDimmed && (
                  <text
                    x={cx} y={cy - r - 4}
                    fill={isHovered ? '#fff' : color}
                    fontSize={isHovered ? 10 : 8}
                    textAnchor="middle"
                    fontWeight={isHovered ? 700 : 500}
                    opacity={isHovered ? 1 : 0.8}
                  >
                    {p.name.split(' ').slice(-1)[0]}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: 'absolute',
          left: Math.min(tooltip.x + 12, W - 180),
          top: Math.max(tooltip.y - 60, 0),
          background: 'var(--bg2)',
          border: `1px solid ${POS_COLOR[tooltip.player.position]}44`,
          borderRadius: 8, padding: '10px 12px',
          pointerEvents: 'none', zIndex: 10,
          minWidth: 160,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <PosBadge pos={tooltip.player.position} />
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{tooltip.player.name}</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.7 }}>
            <div>Age: <span style={{ color: 'var(--text2)' }}>{tooltip.player.age}</span></div>
            <div>Tier: <span style={{ color: 'var(--text2)' }}>T{tooltip.player.tier}</span></div>
            <div>Dynasty Age Score: <span style={{ color: POS_COLOR[tooltip.player.position] }}>{tooltip.player.age_score}</span></div>
            <div>Manager: <span style={{ color: 'var(--text2)' }}>{tooltip.player.manager}</span></div>
            {tooltip.player.years_to_peak > 0 && (
              <div style={{ color: 'var(--green)' }}>{tooltip.player.years_to_peak}yr to peak</div>
            )}
            {tooltip.player.is_declining && (
              <div style={{ color: 'var(--red)' }}>Past prime</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Team Age Score Cards ──────────────────────────────────────────────────────
function TeamAgeCard({ team, isMe, onClick, isSelected }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: isSelected ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${isSelected ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.07)'}`,
        borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
        transition: 'all 0.15s',
        outline: isMe ? `1px solid rgba(var(--accent-rgb),0.3)` : 'none',
      }}
      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        {team.avatar
          ? <img src={`https://sleepercdn.com/avatars/thumbs/${team.avatar}`}
                 alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} />
          : <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'var(--text3)' }}>
              {team.manager?.slice(0, 2).toUpperCase()}
            </div>
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: isMe ? 'var(--accent)' : '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {team.manager} {isMe && <span style={{ fontSize: 9, color: 'var(--accent)' }}>YOU</span>}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text3)' }}>{team.wins}-{team.losses}</div>
        </div>
        <div style={{
          fontSize: 16, fontWeight: 900, color: team.age_color,
          fontFamily: 'monospace',
        }}>
          {team.age_score}
        </div>
      </div>
      <AgeScoreBar score={team.age_score} label={team.age_label} color={team.age_color} />
      {team.avg_age && (
        <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6 }}>
          Avg age: {team.avg_age} · {team.player_count} players
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AgeCurve({ api: apiProp }) {
  const apiBase = apiProp || API;
  const { currentUser } = useUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterPos, setFilterPos] = useState('ALL');
  const [filterManager, setFilterManager] = useState('ALL');
  const [hoveredPlayer, setHoveredPlayer] = useState(null);

  const myRosterId = currentUser?.roster_id;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${apiBase}/api/age-curve`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-filter to current user's team
  useEffect(() => {
    if (data && myRosterId) {
      const myTeam = data.team_summaries?.find(t => t.roster_id === myRosterId);
      if (myTeam) setFilterManager(myTeam.manager);
    }
  }, [data, myRosterId]);

  const managers = data ? ['ALL', ...data.team_summaries.map(t => t.manager)] : ['ALL'];

  const myTeam = data?.team_summaries?.find(t => t.roster_id === myRosterId);

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">Dynasty Age Curve</div>
          <div className="page-subtitle">
            Position-adjusted age value · Hover dots to explore · Dot size = dynasty tier
          </div>
        </div>
        <button className="btn btn-refresh" onClick={fetchData} disabled={loading}>
          {loading ? '⏳' : '↻ Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ background: 'rgba(255,71,87,0.1)', border: '1px solid rgba(255,71,87,0.3)', borderRadius: 8, padding: '12px 16px', color: '#ff6b6b', marginBottom: 16 }}>
          ❌ {error}
        </div>
      )}

      {loading && !data && (
        <div className="loading-state">
          <div className="spinner" />
          <span>Building age curves...</span>
        </div>
      )}

      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, alignItems: 'start' }}>
          {/* Left: Chart */}
          <div>
            {/* My team banner */}
            {myTeam && (
              <div style={{
                background: `${myTeam.age_color}0d`,
                border: `1px solid ${myTeam.age_color}33`,
                borderRadius: 10, padding: '12px 16px', marginBottom: 16,
                display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
              }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 2 }}>Your Roster Age Score</div>
                  <div style={{ fontSize: 28, fontWeight: 900, color: myTeam.age_color, lineHeight: 1 }}>
                    {myTeam.age_score}
                  </div>
                  <div style={{ fontSize: 12, color: myTeam.age_color, marginTop: 2 }}>{myTeam.age_label}</div>
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {['QB', 'RB', 'WR', 'TE'].map(pos => (
                      <div key={pos} style={{ textAlign: 'center' }}>
                        <PosBadge pos={pos} />
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text2)', marginTop: 4 }}>
                          {myTeam.avg_age_by_pos?.[pos] || '—'}
                        </div>
                        <div style={{ fontSize: 9, color: 'var(--text3)' }}>avg age</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>Avg age: {myTeam.avg_age} · {myTeam.player_count} players</div>
              </div>
            )}

            {/* Filters */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1, textTransform: 'uppercase' }}>Position:</span>
              {['ALL', 'QB', 'RB', 'WR', 'TE'].map(pos => (
                <button key={pos} onClick={() => setFilterPos(pos)} style={{
                  padding: '4px 10px', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  border: filterPos === pos ? `1px solid ${POS_COLOR[pos] || 'rgba(255,255,255,0.3)'}` : '1px solid rgba(255,255,255,0.08)',
                  background: filterPos === pos ? (POS_BG[pos] || 'rgba(255,255,255,0.1)') : 'transparent',
                  color: filterPos === pos ? (POS_COLOR[pos] || '#fff') : '#555',
                }}>
                  {pos}
                </button>
              ))}
              <div style={{ width: 1, background: 'rgba(255,255,255,0.08)', margin: '0 4px', height: 16 }} />
              <span style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1, textTransform: 'uppercase' }}>Team:</span>
              <select
                value={filterManager}
                onChange={e => setFilterManager(e.target.value)}
                style={{
                  background: 'var(--bg2)', border: '1px solid rgba(255,255,255,0.1)',
                  color: 'var(--text2)', borderRadius: 5, padding: '4px 8px', fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                {managers.map(m => <option key={m} value={m}>{m === 'ALL' ? 'All Teams' : m}</option>)}
              </select>
            </div>

            {/* Chart */}
            <div style={{
              background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 12, padding: '16px 8px 8px',
            }}>
              <AgeCurveChart
                players={data.players}
                curveLines={data.curve_lines}
                filterPos={filterPos}
                filterManager={filterManager}
                hoveredPlayer={hoveredPlayer}
                setHoveredPlayer={setHoveredPlayer}
              />
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 20, marginTop: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 12 }}>
                {['QB', 'RB', 'WR', 'TE'].map(pos => (
                  <div key={pos} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: POS_COLOR[pos] }} />
                    <span style={{ fontSize: 10, color: 'var(--text3)' }}>{pos}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                {[1, 2, 3].map(tier => (
                  <div key={tier} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: TIER_SIZE[tier] * 1.5, height: TIER_SIZE[tier] * 1.5, borderRadius: '50%', background: '#888', opacity: 0.6 }} />
                    <span style={{ fontSize: 10, color: 'var(--text3)' }}>T{tier}</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 10, color: '#444' }}>Dashed lines = position age curves · Vertical marks = peak age</div>
            </div>
          </div>

          {/* Right: Team Rankings */}
          <div>
            <div className="section-header" style={{ marginBottom: 12 }}>
              <span className="section-header-title">Roster Age Rankings</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.team_summaries.map((team, i) => (
                <TeamAgeCard
                  key={team.roster_id}
                  team={team}
                  isMe={team.roster_id === myRosterId}
                  isSelected={filterManager === team.manager}
                  onClick={() => setFilterManager(filterManager === team.manager ? 'ALL' : team.manager)}
                />
              ))}
            </div>

            {/* Score key */}
            <div style={{
              marginTop: 14, padding: '12px', background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8,
            }}>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 8, letterSpacing: 1, textTransform: 'uppercase' }}>Score Guide</div>
              {[
                { range: '85-100', label: 'Prime Window', color: '#00ff87' },
                { range: '75-84', label: 'Young Core', color: '#4fc3f7' },
                { range: '65-74', label: 'Balanced', color: '#ffd32a' },
                { range: '55-64', label: 'Aging Roster', color: '#ff9800' },
                { range: '<55',   label: 'Veteran Heavy', color: '#ff4757' },
              ].map(r => (
                <div key={r.range} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                  <span style={{ color: r.color }}>{r.label}</span>
                  <span style={{ color: 'var(--text3)' }}>{r.range}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
